import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, Collapse, Grid, IconButton,
  MenuItem, Pagination, Paper, TextField, Tooltip, Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { frFR } from '@mui/x-data-grid/locales';
import {
  Download as DownloadIcon, FilterAltOffOutlined as FilterAltOffIcon,
  FilterAltOutlined as FilterAltIcon, InfoOutlined as InfoIcon,
  RestartAlt as RestartAltIcon, Search as SearchIcon,
} from '@mui/icons-material';
import PartnerMainContainer from '../../../components/PartnerMainContainer';
import LoadingSimpleBackdrop from '../../tools/LoadingSimpleBackdrop';
import TitlePartner from '../component/Title';
import ExportContainer from '../Export/ExportContainer';
import MarcheDetailsModal from '../Procurement/MarcheDetailsModal';
import TerminatedService from '../../../services/TerminatedService';
import { exporterTableauPartenaires } from '../Export/exporterTableauPartenaires';
import {
  formatPartnerSource, normalizeFinancement,
} from '../../../helpers/PartnerHelper';
import usePartnerFilterConsultation from '../hooks/usePartnerFilterConsultation';
import {
  partnerDataGridSx, partnerFilterButtonSx, partnerFilterFieldSx,
  partnerGroupButtonSx, partnerTableColors,
} from '../Parterns/partnerListTheme';

const INVALID_VALUES = ['nan', 'null', '-', 'undefined'];
const GROUP_OPTIONS = [['none', 'Aucun'], ['direction', 'Direction'], ['situation', 'Situation']];

const isValidValue = (value) => {
  if (value === null || value === undefined) return false;
  const text = String(value).trim();
  return text !== '' && !INVALID_VALUES.includes(text.toLowerCase());
};

const normalizeText = (value) => String(value || '').normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '').toUpperCase().trim();

const normalizeSituation = (value) => {
  const normalized = normalizeText(value);
  if (normalized === 'RESILIE' || normalized === 'RESILIEE') return 'RESILIE';
  if (normalized === 'FIN') return 'FIN';
  return normalized || 'RESILIE';
};

const formatDate = (value) => {
  if (!isValidValue(value)) return '—';
  const parts = String(value).split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : String(value);
};


const situationSx = { bgcolor: '#fee4e2', color: '#b42318', fontSize: 10, fontWeight: 800 };

const Terminated = () => {
  const [sourceRows, setSourceRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [filtersVisible, setFiltersVisible] = useState(true);
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const defaultFilters = useMemo(() => ({
    search: '', directions: [], contractFrom: '', contractTo: '',
    ruptureFrom: '', ruptureTo: '', groupBy: 'none', sortBy: 'partenaire',
  }), []);
  const [filters, setFilters] = useState(defaultFilters);

  usePartnerFilterConsultation(filters, (nextFilters) => TerminatedService.getAllRows('terminated', nextFilters));

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    TerminatedService.getAllRows('terminated').then((result) => {
      if (!active) return;
      const data = Array.isArray(result?.data) ? result.data : [];
      setSourceRows(data);
      setLoadError(result?.status === 0 && !data.length ? (result?.message || '') : '');
    }).catch(() => { if (active) setLoadError('Impossible de charger les contrats résiliés.'); })
      .finally(() => { if (active) setIsLoading(false); });
    return () => { active = false; };
  }, []);

  const baseRows = useMemo(() => {
    const markets = new Map();
    sourceRows.forEach((row) => {
      const executionId = Number(row.execution_marche_id) || null;
      const marketNumber = String(row.numeroMarche || row.ref_num_contrat || '').trim();
      if (!executionId || !marketNumber || !isValidValue(row.partenaire)) return;
      const key = `EXEC:${executionId}`;
      const current = markets.get(key) || {
        id: key,
        execution_marche_id: executionId,
        id_marche_spm: Number(row.id_marche_spm) || null,
        partenaire_id: Number(row.partenaire_id) || null,
        partenaire: String(row.partenaire).trim(),
        numero: marketNumber,
        numeroMarche: marketNumber,
        ref_num_contrat: row.ref_num_contrat || marketNumber,
        direction: String(row.direction || '').trim() || '—',
        financement: normalizeFinancement(row.financement) || '—',
        dateDebut: row.dateDebut || null,
        dateFin: row.dateFin || null,
        dateRupture: row.dateRupture || null,
        causeRupture: isValidValue(row.causeRupture) ? String(row.causeRupture).trim() : '—',
        causeRuptureNarratif: isValidValue(row.causeRuptureNarratif) ? String(row.causeRuptureNarratif).trim() : '',
        situation: normalizeSituation(row.situation),
        source_db: row.source_db || 'bdd partenaire',
      };
      if (!current.dateRupture && row.dateRupture) current.dateRupture = row.dateRupture;
      if (current.causeRupture === '—' && isValidValue(row.causeRupture)) current.causeRupture = String(row.causeRupture).trim();
      markets.set(key, current);
    });
    return Array.from(markets.values());
  }, [sourceRows]);

  const directionOptions = useMemo(() => [...new Set(baseRows.map((row) => row.direction).filter((value) => value !== '—'))].sort(), [baseRows]);

  const rows = useMemo(() => baseRows.filter((row) => {
    const searchText = normalizeText(`${row.partenaire} ${row.numero} ${row.causeRupture}`);
    if (filters.search && !searchText.includes(normalizeText(filters.search))) return false;
    if (filters.directions.length && !filters.directions.includes(row.direction)) return false;
    if (filters.contractFrom && row.dateFin && row.dateFin < filters.contractFrom) return false;
    if (filters.contractTo && row.dateDebut && row.dateDebut > filters.contractTo) return false;
    if (filters.ruptureFrom && (!row.dateRupture || row.dateRupture < filters.ruptureFrom)) return false;
    if (filters.ruptureTo && (!row.dateRupture || row.dateRupture > filters.ruptureTo)) return false;
    return true;
  }).sort((left, right) => {
    if (filters.sortBy === 'dateFin') return String(left.dateFin || '').localeCompare(String(right.dateFin || ''));
    if (filters.sortBy === 'dateRupture') return String(left.dateRupture || '').localeCompare(String(right.dateRupture || ''));
    return left.partenaire.localeCompare(right.partenaire, 'fr');
  }), [baseRows, filters]);

  useEffect(() => setPage(0), [filters, pageSize]);

  const groupedRows = useMemo(() => {
    if (filters.groupBy === 'none') return [];
    const groups = new Map();
    rows.forEach((row) => {
      const label = filters.groupBy === 'direction' ? row.direction : row.situation;
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label).push(row);
    });
    return Array.from(groups.entries()).sort(([left], [right]) => left.localeCompare(right, 'fr'));
  }, [filters.groupBy, rows]);

  const groupedPages = useMemo(() => {
    if (filters.groupBy === 'none') return [];
    const pages = [];
    let currentPage = [];
    let marketCount = 0;
    groupedRows.forEach(([label, groupRows], index) => {
      if (marketCount > 0 && marketCount + groupRows.length > pageSize) {
        pages.push(currentPage); currentPage = []; marketCount = 0;
      }
      currentPage.push({ id: `GROUP:${index}`, isGroup: true, label, count: groupRows.length });
      currentPage.push(...groupRows);
      marketCount += groupRows.length;
    });
    if (currentPage.length) pages.push(currentPage);
    return pages;
  }, [filters.groupBy, groupedRows, pageSize]);

  const displayedRows = filters.groupBy === 'none' ? rows : (groupedPages[page] || groupedPages[0] || []);
  const activeFiltersCount = filters.directions.length
    + ['search', 'contractFrom', 'contractTo', 'ruptureFrom', 'ruptureTo'].filter((field) => Boolean(filters[field])).length
    + (filters.groupBy !== 'none' ? 1 : 0);

  const openDetail = (row) => {
    if (!row || row.isGroup) return;
    setSelectedMarket(row); setDetailOpen(true);
  };

  const columns = [
    { field: 'partenaire', headerName: 'Partenaire', minWidth: 190, flex: 1.1, renderCell: ({ row, value }) => row.isGroup ? <Typography sx={{ fontSize: 12, fontWeight: 800 }}>{row.label} ({row.count})</Typography> : value },
    { field: 'numero', headerName: 'N° marché', minWidth: 145, flex: 0.8, renderCell: ({ row, value }) => row.isGroup ? null : value },
    { field: 'direction', headerName: 'Direction', minWidth: 105, flex: 0.6, renderCell: ({ row, value }) => row.isGroup ? null : value },
    { field: 'financement', headerName: 'Financement', minWidth: 120, flex: 0.7, renderCell: ({ row, value }) => row.isGroup ? null : value },
    { field: 'dateDebut', headerName: 'Début', minWidth: 105, renderCell: ({ row, value }) => row.isGroup ? null : formatDate(value) },
    { field: 'dateFin', headerName: 'Fin', minWidth: 105, renderCell: ({ row, value }) => row.isGroup ? null : formatDate(value) },
    { field: 'dateRupture', headerName: 'Date rupture', minWidth: 120, renderCell: ({ row, value }) => row.isGroup ? null : formatDate(value) },
    { field: 'causeRupture', headerName: 'Cause rupture', minWidth: 190, flex: 1.1, renderCell: ({ row, value }) => row.isGroup ? null : (value || '—') },
    { field: 'situation', headerName: 'Situation', minWidth: 115, renderCell: ({ row, value }) => row.isGroup ? null : <Chip size="small" label={value || 'RESILIE'} sx={situationSx} /> },
    { field: 'source_db', headerName: 'Base source', minWidth: 165, flex: 0.9, renderCell: ({ row, value }) => row.isGroup ? null : formatPartnerSource(value) },
    { field: 'details', headerName: 'Détails', width: 82, sortable: false, filterable: false, align: 'center', headerAlign: 'center', renderCell: ({ row }) => row.isGroup ? null : <Tooltip title="Afficher le contrat"><IconButton size="small" onClick={(event) => { event.stopPropagation(); openDetail(row); }} onDoubleClick={(event) => event.stopPropagation()}><InfoIcon fontSize="small" /></IconButton></Tooltip> },
  ];

  const exportRows = rows.map((row) => ({
    Partenaire: row.partenaire, 'N° marché': row.numero, Direction: row.direction,
    Financement: row.financement, Début: formatDate(row.dateDebut), Fin: formatDate(row.dateFin),
    'Date rupture': formatDate(row.dateRupture), 'Cause rupture': row.causeRupture,
    Situation: row.situation, 'Base source': formatPartnerSource(row.source_db),
  }));
  const exporter = (format) => exporterTableauPartenaires({
    format, lignes: exportRows,
    entetes: ['Partenaire', 'N° marché', 'Direction', 'Financement', 'Début', 'Fin',
      'Date rupture', 'Cause rupture', 'Situation', 'Base source'],
    titre: 'Contrats résiliés', nomFichier: 'contrats_resilies', nomFeuille: 'Contrats résiliés',
    resume: `${rows.length} contrat(s)`,
    audit: { menu: 'Contrats resilies', format: format === 'xlsx' ? 'excel' : format,
      filters, rowCount: rows.length },
  });
  const handleExcelExport = () => exporter('xlsx');
  const handleCsvExport = () => exporter('csv');
  const handlePdfExport = () => exporter('pdf');
  const handlePrint = () => exporter('print');

  return <PartnerMainContainer>
    <LoadingSimpleBackdrop openLoadingSimpleBackdrop={isLoading} />
    <TitlePartner title="Contrats résiliés" sub={`(${rows.length} contrats)`} icon="🚫" />
    {loadError && <Alert severity="error" sx={{ mb: 1.5 }}>{loadError}</Alert>}

    <Paper elevation={0} sx={{ border: '1px solid #cbd8e6', borderRadius: '8px', mb: 2, p: 1.25 }}>
      <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, justifyContent: 'space-between', mb: filtersVisible ? 1.5 : 0 }}>
        <Box><Typography color="#52677c" fontSize={10} fontWeight={700} letterSpacing="0.05em">FILTRES</Typography><Typography color="#667085" fontSize={10}>{activeFiltersCount} critère(s) actif(s)</Typography></Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: { xs: '100%', sm: 'auto' } }}>
          {filtersVisible && <Button disabled={activeFiltersCount === 0} onClick={() => setFilters(defaultFilters)} size="small" startIcon={<RestartAltIcon />} sx={{ ...partnerFilterButtonSx, borderColor: '#9fb1c3', color: partnerTableColors.dark }} variant="outlined">Réinitialiser</Button>}
          <Button onClick={() => setFiltersVisible((value) => !value)} size="small" startIcon={filtersVisible ? <FilterAltOffIcon /> : <FilterAltIcon />} sx={{ ...partnerFilterButtonSx, borderColor: '#9fb1c3', color: partnerTableColors.dark }} variant="outlined">{filtersVisible ? 'Masquer filtres' : 'Afficher filtres'}</Button>
          <Button disabled={!rows.length} onClick={(event) => setExportAnchorEl(event.currentTarget)} size="small" startIcon={<DownloadIcon />} sx={{ ...partnerFilterButtonSx, bgcolor: partnerTableColors.gold, color: partnerTableColors.text, fontWeight: 800, '&:hover': { bgcolor: '#c39b2e' } }} variant="contained">Exporter</Button>
        </Box>
      </Box>
      <Collapse in={filtersVisible} timeout="auto" unmountOnExit>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Grid container spacing={1.25}>
            <Grid item xs={12} md={5}><TextField fullWidth size="small" placeholder="Partenaire, marché ou cause..." value={filters.search} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} sx={partnerFilterFieldSx} InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: '#667085' }} /> }} /></Grid>
            <Grid item xs={12} sm={6} md={3}><Autocomplete multiple disableCloseOnSelect size="small" options={directionOptions} value={filters.directions} onChange={(_, value) => setFilters((current) => ({ ...current, directions: value }))} renderInput={(params) => <TextField {...params} label="Direction" sx={partnerFilterFieldSx} />} /></Grid>
            <Grid item xs={12} sm={6} md={2}><TextField select fullWidth label="Trier par" size="small" value={filters.sortBy} onChange={(event) => setFilters((current) => ({ ...current, sortBy: event.target.value }))} sx={partnerFilterFieldSx}><MenuItem value="partenaire">Partenaire</MenuItem><MenuItem value="dateFin">Fin du contrat</MenuItem><MenuItem value="dateRupture">Date de rupture</MenuItem></TextField></Grid>
          </Grid>
          <Box sx={{ alignItems: 'center', display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 1.25 }}>
            <Box sx={{ alignItems: 'center', border: `1px solid ${partnerTableColors.border}`, borderRadius: '6px', display: 'flex', flex: 1, flexWrap: 'wrap', gap: 0.75, p: 1, width: '100%' }}><Typography color="#52677c" fontSize={11} fontWeight={700}>Grouper par :</Typography>{GROUP_OPTIONS.map(([value, label]) => <Button key={value} onClick={() => setFilters((current) => ({ ...current, groupBy: value }))} size="small" sx={partnerGroupButtonSx} variant={filters.groupBy === value ? 'contained' : 'outlined'}>{label}</Button>)}</Box>
            <Box sx={{ alignItems: 'center', border: `1px solid ${partnerTableColors.border}`, borderRadius: '6px', display: 'flex', flexWrap: 'wrap', gap: 1, p: 1, width: { xs: '100%', lg: 'auto' } }}><Typography color="#52677c" fontSize={11} fontWeight={700}>Période du contrat :</Typography><TextField type="date" size="small" value={filters.contractFrom} onChange={(event) => setFilters((current) => ({ ...current, contractFrom: event.target.value }))} sx={{ ...partnerFilterFieldSx, width: { xs: '100%', sm: 155 } }} /><Typography>→</Typography><TextField type="date" size="small" value={filters.contractTo} onChange={(event) => setFilters((current) => ({ ...current, contractTo: event.target.value }))} sx={{ ...partnerFilterFieldSx, width: { xs: '100%', sm: 155 } }} /></Box>
            <Box sx={{ alignItems: 'center', border: `1px solid ${partnerTableColors.border}`, borderRadius: '6px', display: 'flex', flexWrap: 'wrap', gap: 1, p: 1, width: { xs: '100%', lg: 'auto' } }}><Typography color="#52677c" fontSize={11} fontWeight={700}>Période de rupture :</Typography><TextField type="date" size="small" value={filters.ruptureFrom} onChange={(event) => setFilters((current) => ({ ...current, ruptureFrom: event.target.value }))} sx={{ ...partnerFilterFieldSx, width: { xs: '100%', sm: 155 } }} /><Typography>→</Typography><TextField type="date" size="small" value={filters.ruptureTo} onChange={(event) => setFilters((current) => ({ ...current, ruptureTo: event.target.value }))} sx={{ ...partnerFilterFieldSx, width: { xs: '100%', sm: 155 } }} /></Box>
          </Box>
        </Box>
      </Collapse>
    </Paper>

    <Paper elevation={0} sx={{ border: '1px solid #cbd8e6', borderRadius: '8px', overflow: 'hidden' }}>
      <Box sx={{ alignItems: 'center', bgcolor: '#f8fbff', borderBottom: `1px solid ${partnerTableColors.border}`, display: 'flex', justifyContent: 'space-between', px: 1.75, py: 1.5 }}><Typography sx={{ color: '#1f2a44', fontSize: 13, fontWeight: 600 }}>Liste des contrats résiliés — 1 ligne = 1 contrat</Typography><Typography sx={{ color: '#4b6488', fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{rows.length} contrat(s)</Typography></Box>
      <Box sx={{ height: 560 }}><DataGrid rows={displayedRows} columns={columns} columnHeaderHeight={48} disableColumnSorting={filters.groupBy !== 'none'} disableRowSelectionOnClick getRowClassName={({ row }) => row.isGroup ? 'partner-group-row' : ''} hideFooter={filters.groupBy !== 'none'} localeText={frFR.components.MuiDataGrid.defaultProps.localeText} onPaginationModelChange={filters.groupBy === 'none' ? (model) => { setPage(model.page); setPageSize(model.pageSize); } : undefined} onRowDoubleClick={({ row }) => openDetail(row)} pageSizeOptions={[10, 15, 30, 50]} pagination={filters.groupBy === 'none'} paginationModel={filters.groupBy === 'none' ? { page, pageSize } : undefined} rowHeight={48} sx={partnerDataGridSx} /></Box>
      {filters.groupBy !== 'none' && <Box sx={{ alignItems: 'center', borderTop: `1px solid ${partnerTableColors.border}`, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, justifyContent: 'space-between', px: 1.5, py: 1 }}><Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}><Typography color="#52677c" fontSize={11}>Cible contrats par page</Typography><TextField select size="small" value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }} sx={{ ...partnerFilterFieldSx, width: 82 }}>{[10, 15, 30, 50].map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}</TextField></Box><Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}><Typography color="#52677c" fontSize={11}>Page {Math.min(page + 1, Math.max(groupedPages.length, 1))} / {Math.max(groupedPages.length, 1)}</Typography><Pagination count={Math.max(groupedPages.length, 1)} page={Math.min(page + 1, Math.max(groupedPages.length, 1))} onChange={(_, nextPage) => setPage(nextPage - 1)} size="small" /></Box></Box>}
    </Paper>

    <ExportContainer exportAnchorEl={exportAnchorEl} handleExportClose={() => setExportAnchorEl(null)} handlePrint={handlePrint} handleExcelExport={handleExcelExport} handleCsvExport={handleCsvExport} handlePdfExport={handlePdfExport} />
    <MarcheDetailsModal open={detailOpen} market={selectedMarket} onClose={() => setDetailOpen(false)} />
  </PartnerMainContainer>;
};

export default Terminated;
